import parse from 'html-react-parser';

import ApiState from '@/components/ApiState/ApiState';
import Layout from '@/components/Layout/Layout';
import Link from '@/components/LangLink/LangLink';
import NavLabel from '@/components/NavLabel/NavLabel';
import Overlay from '@/components/Overlay/Overlay';
import { EXPO_CONFIG } from '@/api/expoConfig';
import { useBodyClass } from '@/hooks/useBodyClass';
import { useBasicPage } from '@/api/useThesaurusTerm';
import { useNavigationStore } from '@/store/useNavigationStore';
import { splitTerm } from '@/utils/splitTerm';

import fulla from '@/assets/img/overlays/overlay-fulla.png';
import textElio from '@/assets/img/overlays/overlay-text-elio.png';

function Home() {
    useBodyClass('home');

    const pages = useNavigationStore((s) => s.pages);
    const isReady = useNavigationStore((s) => s.isReady);

    const [huir, vivir, regresar, irse] = useNavigationStore((s) => s.pageGroups);
    const huirPages = pages.filter((page) => page.pageGroup === huir?.section_id);
    const vivirPages = pages.filter((page) => page.pageGroup === vivir?.section_id);
    const regresarPages = pages.filter((page) => page.pageGroup === regresar?.section_id);
    const irsePages = pages.filter((page) => page.pageGroup === irse?.section_id);
    const timelinePage = pages.find((page) => page.type === 'timeline');
    const learnMorePage = pages.find((page) => page.type === 'learnMore');

    const { data, isLoading, error } = useBasicPage(EXPO_CONFIG.rootId);
    const pageContent = data?.[0];
    const { title, abstract, body } = pageContent ?? {};
    const { primary: titlePrimary, secondary: titleSecondary } = splitTerm(title, '|');

    return (
        <Layout page="home">
            <div className="home--hero">
                <div className="is-flex is-flex-direction-column gap-5 py-9">
                    <ApiState isLoading={isLoading} error={error}>
                        <>
                            {titlePrimary && (
                                <h1 className="fluid-34-68 is-uppercase has-text-primary is-family-secondary">
                                    {titlePrimary}
                                </h1>
                            )}
                            {titleSecondary && (
                                <h2 className="is-size-1 has-text-weight-semibold is-uppercase">
                                    {titleSecondary}
                                </h2>
                            )}
                            {abstract && <p className="has-text-weight-light">{parse(abstract)}</p>}
                        </>
                    </ApiState>
                </div>
            </div>
            <div className="wrapper overlay-wrapper">
                <div className="overlay-space is-hidden-touch">
                    <Overlay
                        src={textElio}
                        style={{ top: -50, right: -250, zIndex: 9, maxWidth: 486 }}
                    />
                    <Overlay
                        src={fulla}
                        style={{ top: 100, right: 130, zIndex: 10, maxWidth: 440 }}
                    />
                </div>
                <div className="overlay-space is-hidden-desktop">
                    <Overlay
                        src={textElio}
                        style={{
                            top: -150,
                            right: 0,
                            zIndex: 9,
                            maxWidth: 486,
                        }}
                    />
                    <Overlay
                        src={fulla}
                        style={{
                            top: -40,
                            left: 0,
                            zIndex: 10,
                            maxWidth: 440,
                        }}
                    />
                </div>
            </div>
            <div className="home--divider" />
            <div className="wrapper has-text-weight-light py-5 mt-5">
                <div className="columns is-desktop">
                    <div className="column is-one-third-desktop">
                        <ApiState isLoading={isLoading} error={error}>
                            {body && <p>{parse(body)}</p>}
                        </ApiState>
                    </div>
                    {isReady && (
                        <>
                            <div className="column is-one-third-desktop flow">
                                <h2>{huir.title}</h2>
                                <ul className="list-reset boxes-list boxes-list-cols-1">
                                    {huirPages.map((page) => {
                                        const { primary, secondary } = splitTerm(page.term);
                                        return (
                                            <li key={page.section_id} className="full-link">
                                                <Link to={page.web_path}>
                                                    <NavLabel
                                                        primary={primary}
                                                        secondary={secondary}
                                                        inline
                                                    />
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <h2>{vivir.title}</h2>
                                <ul className="list-reset boxes-list boxes-list-cols-1">
                                    {vivirPages.map((page) => {
                                        const { primary, secondary } = splitTerm(page.term);
                                        return (
                                            <li key={page.section_id} className="full-link">
                                                <Link to={page.web_path}>
                                                    <NavLabel
                                                        primary={primary}
                                                        secondary={secondary}
                                                        inline
                                                    />
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                            <div className="column is-one-third-desktop flow">
                                <h2>{regresar.title}</h2>
                                <ul className="list-reset boxes-list boxes-list-cols-1">
                                    {regresarPages.map((page) => {
                                        const { primary, secondary } = splitTerm(page.term);
                                        return (
                                            <li key={page.section_id} className="full-link">
                                                <Link to={page.web_path}>
                                                    <NavLabel
                                                        primary={primary}
                                                        secondary={secondary}
                                                        inline
                                                    />
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <h2>{irse.title}</h2>
                                <ul className="list-reset boxes-list boxes-list-cols-1">
                                    {irsePages.map((page) => {
                                        const { primary, secondary } = splitTerm(page.term);
                                        return (
                                            <li key={page.section_id} className="full-link">
                                                <Link to={page.web_path}>
                                                    <NavLabel
                                                        primary={primary}
                                                        secondary={secondary}
                                                        inline
                                                    />
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <ul className="list-reset boxes-list boxes-list-cols-2 boxes-list--dest pt-8">
                                    {[timelinePage, learnMorePage].map((page) => {
                                        const { primary, secondary } = splitTerm(page.term);
                                        return (
                                            <li key={page.section_id} className="full-link">
                                                <Link to={page.web_path}>
                                                    <NavLabel
                                                        primary={primary}
                                                        secondary={secondary}
                                                        inline
                                                    />
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default Home;
